import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, ChevronDown, X } from 'lucide-react';
import api from '@/services/api';
import { productService } from '@/services/product.service';
import { Product, Category, Brand } from '@/types';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import { Button } from '@/components/ui/button';

export const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const brandFilter = searchParams.get('brand') || undefined;
  const sort = searchParams.get('sort') || 'createdAt';
  const rxOnly = searchParams.get('rx') === 'true';
  const inStockOnly = searchParams.get('instock') === 'true';
  const LIMIT = 20;

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      api.get(`/categories/${slug}/products`),
      api.get('/brands'),
    ]).then(([catRes, brandRes]) => {
      setCategory(catRes.data.data.category);
      setBrands(brandRes.data.data);
    });
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    productService.getProducts({
      category: slug,
      brand: brandFilter,
      sort,
      requiresPrescription: rxOnly || undefined,
      inStock: inStockOnly || undefined,
      page,
      limit: LIMIT,
    }).then(res => {
      setProducts(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    }).finally(() => setLoading(false));
  }, [slug, brandFilter, sort, rxOnly, inStockOnly, page]);

  const setParam = (key: string, value: string | null) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: category?.name || '...' }]} />

      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">{category?.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{total} products</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={sort}
            onChange={e => setParam('sort', e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            <option value="createdAt">Latest</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => setFiltersOpen(!filtersOpen)} className="md:hidden">
            <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar filters */}
        <aside className={`w-64 shrink-0 space-y-6 ${filtersOpen ? 'block' : 'hidden'} md:block`}>
          {/* Brands */}
          {brands.length > 0 && (
            <div className="bg-white rounded-card shadow-card p-4">
              <h3 className="font-heading font-semibold text-gray-800 mb-3">Brand</h3>
              <div className="space-y-2">
                {brands.map(b => (
                  <label key={b.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="brand"
                      checked={brandFilter === b.id}
                      onChange={() => setParam('brand', b.id)}
                      className="accent-primary-500"
                    />
                    <span className="text-sm text-gray-700">{b.name}</span>
                  </label>
                ))}
                {brandFilter && (
                  <button onClick={() => setParam('brand', null)} className="text-xs text-primary-600 mt-1">Clear</button>
                )}
              </div>
            </div>
          )}

          {/* Toggles */}
          <div className="bg-white rounded-card shadow-card p-4 space-y-3">
            <h3 className="font-heading font-semibold text-gray-800 mb-2">Filters</h3>
            <Toggle label="In Stock Only" checked={inStockOnly} onChange={v => setParam('instock', v ? 'true' : null)} />
            <Toggle label="Prescription Required" checked={rxOnly} onChange={v => setParam('rx', v ? 'true' : null)} />
          </div>
        </aside>

        {/* Products */}
        <div className="flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {loading
              ? Array(12).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)
              : products.map(p => <ProductCard key={p.id} product={p} />)
            }
          </div>

          {!loading && products.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500">No products found. Try adjusting your filters.</p>
              <Button variant="outline" className="mt-4" onClick={() => setSearchParams({})}>Clear Filters</Button>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setParam('page', String(p))}
                  className={`w-9 h-9 rounded-xl text-sm font-medium transition-all ${p === page ? 'bg-primary-500 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-primary-300'}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center justify-between cursor-pointer">
    <span className="text-sm text-gray-700">{label}</span>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-all ${checked ? 'bg-primary-500' : 'bg-gray-200'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  </label>
);
